import { Component, EventEmitter, Output, Input, ViewChild, ElementRef, OnInit } from '@angular/core';
import { HttpClient, HttpEvent, HttpEventType, HttpHeaders } from '@angular/common/http';
import { AuthenticationService } from '../auth/authentication.service';
import { APP_CONST } from '../../app.const';

@Component({
  selector: 'app-img-upload-v2',
  template: `
    <div class="img-upload-container">
      <input #fileInput type="file" accept="image/*" (change)="onFileSelected($event)" />
      <div *ngIf="previewUrl" class="img-preview">
        <img [src]="previewUrl" alt="Image preview" />
        <button type="button" class="remove-btn" (click)="removeImage()">Remove</button>
      </div>
      <div *ngIf="uploading">
        <div class="progress-bar-container">
          <div class="progress-bar" [style.width.%]="progress"></div>
        </div>
        Uploading... {{ progress }}%
      </div>
      <div *ngIf="uploadSuccess">✅ Upload success!</div>
      <div *ngIf="uploadFailed">⚠️ Upload failed!</div>
    </div>
  `,
  styles: [
    `
      .img-upload-container {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .img-preview img {
        max-width: 200px;
        max-height: 200px;
        display: block;
      }
      .img-preview .remove-btn {
        margin-top: 8px;
        padding: 6px 16px;
        background-color: #e53935;
        color: #fff;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        transition: background 0.2s;
      }
      .img-preview .remove-btn:hover {
        background-color: #b71c1c;
      }
      .progress-bar-container {
        width: 100%;
        background: #eee;
        border-radius: 4px;
        height: 8x;
        margin-bottom: 4px;
        overflow: hidden;
      }
      .progress-bar {
        height: 100%;
        background: #1976d2;
        transition: width 0.2s;
      }
    `
  ]
})
export class ImgUploadV2Component implements OnInit {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  ngOnInit(): void {
    // this.resetComponent();
  }

  @Input() set previewImage(value: string | null) {
    if (!value) {
      this.resetComponent();
    } else {
      this.previewUrl = value;
    }
  }
  @Output() fieldChange = new EventEmitter<string>();
  @Output() previewImageChange = new EventEmitter<string>();
  @Input() field: string = '';
  @Output() imageSelected = new EventEmitter<File>();
  previewUrl: string | ArrayBuffer | null = null;
  private selectedFile: File | null = null;
  uploading = false;
  uploadSuccess = false;
  uploadFailed = false;
  progress = 0;
  maxFileSize = 5 * 1024 * 1024; // 5 MB

  constructor(private http: HttpClient, private authService: AuthenticationService) {}

  // Not yet used in this component, but can be used to render a presigned URL
  renderPresignedUrl(): string {
    console.log('Rendering presigned URL for:', this.previewImage);
    if (this.previewImage) {
      const token = this.authService.user?.accessToken || '';
      const filePayload = { storageFileName: this.previewImage };
      this.http
        .post<{ url: string }>(`${APP_CONST.API_MASTER}/admin/spk/pre-signed-url`, filePayload, {
          headers: new HttpHeaders({
            Authorization: `Bearer ${token}`
          })
        })
        .subscribe({
          next: response => {
            this.previewUrl = response.url;
          },
          error: err => {
            console.error('Error fetching presigned URL:', err);
            this.previewUrl = null; // Reset previewUrl on error
          }
        });
    }

    return this.previewImage || '';
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.selectedFile = input.files[0];
      const reader = new FileReader();
      reader.onload = () => (this.previewUrl = reader.result);
      reader.readAsDataURL(this.selectedFile);
      this.imageSelected.emit(this.selectedFile);

      this.uploadFile(this.selectedFile); // auto upload
    }
  }

  uploadFile(file: File): void {
    if (!file || file.size > this.maxFileSize) {
      alert(`File is too large. Maximum size is ${this.maxFileSize / 1024 / 1024} MB.`);
      return;
    }
    const formData = new FormData();
    formData.append('file', file);

    const token = this.authService.user?.accessToken || '';

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });

    this.uploading = true;
    this.uploadSuccess = false;
    this.uploadFailed = false;
    this.progress = 0;

    this.http
      .post(APP_CONST.API_FILE, formData, {
        headers,
        reportProgress: true,
        observe: 'events'
      })
      .subscribe({
        next: (event: HttpEvent<any>) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            this.progress = Math.round((100 * event.loaded) / event.total);
          }
          if (event.type === HttpEventType.Response) {
            this.uploading = false;
            this.progress = 100;

            const storageFileName = event.body?.storageFileName;
            const fileUrl = event.body?.fileUrl; // ambil dari backend

            if (storageFileName && fileUrl) {
              this.previewUrl = fileUrl; // set preview dari backend
              this.uploadSuccess = true;
              this.uploadFailed = false;
              this.field = storageFileName;
              this.fieldChange.emit(storageFileName);
              if (fileUrl) {
                this.previewImageChange.emit(fileUrl);
              }
            } else {
              this.previewUrl = null;
              this.uploadSuccess = false;
              this.fieldChange.emit('');
              this.uploadFailed = true;
              this.fileInput.nativeElement.value = ''; // Reset file input after successful upload
            }
          }
        },
        error: err => {
          this.uploading = false;
          this.uploadSuccess = false;
          this.uploadFailed = true;
          this.progress = 0;
          console.error('Upload failed', err);
          this.fileInput.nativeElement.value = ''; // Reset file input to allow reupload of the same file
        }
      });
  }

  removeImage(): void {
    this.resetComponent();
    this.imageSelected.emit(null as any);
    this.fieldChange.emit('');
  }

  private resetComponent(): void {
    this.selectedFile = null;
    this.previewUrl = null;
    this.uploadSuccess = false;
    this.uploadFailed = false;
    this.progress = 0;
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }
}
